import { useState } from 'react';
import { X, CheckCircle, XCircle } from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';

interface ValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (approved: boolean, notes: string) => void;
  isLoading: boolean;
  investigationId: string;
  employeeName: string;
}

export default function ValidationModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  employeeName,
}: ValidationModalProps) {
  const [validationNotes, setValidationNotes] = useState('');
  const [validationType, setValidationType] = useState<'approve' | 'reject' | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validationType === 'reject' && !validationNotes.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    onSubmit(validationType === 'approve', validationNotes);
    handleClose();
  };

  const handleClose = () => {
    setValidationNotes('');
    setValidationType(null);
    onClose();
  };

  const handleApprove = () => {
    setValidationType('approve');
  };

  const handleReject = () => {
    setValidationType('reject');
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 overflow-hidden">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Validate Investigation Report</h2>
            <button
              onClick={handleClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
              {/* Employee Info */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-600 mb-1">Employee Name</p>
                <p className="text-base font-semibold text-gray-900">{employeeName}</p>
              </div>

              {/* Action Selection */}
              {!validationType && (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-900">Select Action</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={handleApprove}
                      className="flex flex-col items-center justify-center gap-3 p-6 border-2 border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-all group"
                    >
                      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors">
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">Approve</span>
                    </button>

                    <button
                      type="button"
                      onClick={handleReject}
                      className="flex flex-col items-center justify-center gap-3 p-6 border-2 border-gray-200 rounded-xl hover:border-red-500 hover:bg-red-50 transition-all group"
                    >
                      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center group-hover:bg-red-200 transition-colors">
                        <XCircle className="w-6 h-6 text-red-600" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">Reject</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Validation Notes */}
              {validationType && (
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {validationType === 'approve' ? (
                        <>
                          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          </div>
                          <span className="text-sm font-medium text-gray-900">Approving Investigation</span>
                        </>
                      ) : (
                        <>
                          <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                            <XCircle className="w-4 h-4 text-red-600" />
                          </div>
                          <span className="text-sm font-medium text-gray-900">Rejecting Investigation</span>
                        </>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setValidationType(null)}
                      className="text-xs text-gray-500 hover:text-gray-700 underline"
                    >
                      Change
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      {validationType === 'approve' ? 'Validation Notes (Optional)' : 'Reason for Rejection *'}
                    </label>
                    <textarea
                      value={validationNotes}
                      onChange={(e) => setValidationNotes(e.target.value)}
                      placeholder={
                        validationType === 'approve'
                          ? 'Add any notes or comments about this validation...'
                          : 'Explain what needs to be revised or corrected...'
                      }
                      rows={6}
                      required={validationType === 'reject'}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {validationNotes.length}/500 characters
                    </p>
                  </div>

                  {/* Info Box */}
                  <div className={`rounded-xl p-4 ${
                    validationType === 'approve' 
                      ? 'bg-green-50 border border-green-100' 
                      : 'bg-orange-50 border border-orange-100'
                  }`}>
                    <p className={`text-xs font-medium mb-2 ${
                      validationType === 'approve' ? 'text-green-900' : 'text-orange-900'
                    }`}>
                      What happens next:
                    </p>
                    <ul className={`text-xs space-y-1 list-disc list-inside ${
                      validationType === 'approve' ? 'text-green-800' : 'text-orange-800'
                    }`}>
                      {validationType === 'approve' ? (
                        <>
                          <li>Investigation status changes to COMPLETED</li>
                          <li>Earning record is created for the agent</li>
                          <li>Agent and employer receive email notifications</li>
                        </>
                      ) : (
                        <>
                          <li>Investigation status returns to IN_PROGRESS</li>
                          <li>Agent receives notification with your feedback</li>
                          <li>Agent can revise and resubmit the report</li>
                        </>
                      )}
                    </ul>
                  </div>
                </>
              )}
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <Button
                type="button"
                onClick={handleClose}
                variant="outline"
                className="flex-1 px-6 py-2.5 border-gray-200 text-gray-700 hover:bg-gray-50 rounded-xl text-sm font-medium"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading || !validationType}
                className={`flex-1 px-6 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-50 ${
                  validationType === 'approve'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {isLoading ? 'Processing...' : validationType === 'approve' ? 'Approve & Create Earning' : 'Reject & Send Feedback'}
              </Button>
            </div>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
}
